import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {
    AGENT_TYPE_FORM_GROUP_KEY_MAP,
    AGENT_TYPE_OPERATOR_MAP,
    CONSUMPTION_AGENT_TYPE_INVESTIGATIONS,
    ConsumptionAgentInvestigationFormGroup,
    ConsumptionAgentType,
    ConsumptionAgentTypeInvestigation,
} from './consumption-agent-investigation.model';
import {
    filterNullishRx,
    fromControl,
    fromControlWithDebounceTime,
    TranslatePipe,
    TranslateService,
    Translation,
} from '@brunata-angular/util';
import {ConsumptionToMonitor} from '../consumption-agent-monitoring/consumption-agent-monitoring.model';
import {
    BehaviorSubject,
    combineLatest,
    map,
    Observable,
    of,
    ReplaySubject,
    startWith,
    switchMap,
} from 'rxjs';
import {AsyncPipe} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {InputNumberGroupComponent} from '@brunata-angular/ui';
import {TooltipComponent} from '@brunata-angular/ui';
import {take} from 'rxjs/operators';

type AgentInvestigationView = {
    formGroup: ConsumptionAgentInvestigationFormGroup;
    consumptionToMonitor: ConsumptionToMonitor;
    consumptionAgentTypeInvestigations: ConsumptionAgentTypeInvestigation[];
};

@Component({
    selector: 'app-consumption-agent-investigation',
    standalone: true,
    imports: [
        FormsModule,
        InputNumberGroupComponent,
        ReactiveFormsModule,
        TranslatePipe,
        AsyncPipe,
        TooltipComponent,
    ],
    templateUrl: './consumption-agent-investigation.component.html',
    styleUrl: './consumption-agent-investigation.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsumptionAgentInvestigationComponent {
    @Input({required: true}) set consumptionToMonitor(consumptionToMonitor: ConsumptionToMonitor) {
        this.consumptionToMonitorSubject$.next(consumptionToMonitor);
    }

    @Input({required: true}) set formGroup(formGroup: ConsumptionAgentInvestigationFormGroup) {
        this.formGroupSubject$.next(formGroup);
    }

    @Input({required: true}) set outputUnitTranslated(outputUnitTranslated: string) {
        this.outputUnitTranslatedSubject$.next(outputUnitTranslated);
    }

    private readonly translateService = inject(TranslateService);

    private readonly consumptionToMonitorSubject$ = new ReplaySubject<ConsumptionToMonitor>(1);
    private readonly consumptionToMonitor$ =
        this.consumptionToMonitorSubject$.pipe(filterNullishRx());

    private readonly formGroupSubject$ = new ReplaySubject<ConsumptionAgentInvestigationFormGroup>(
        1,
    );
    private readonly formGroup$ = this.formGroupSubject$.pipe(filterNullishRx());

    private readonly outputUnitTranslatedSubject$ = new BehaviorSubject<string>('');

    private readonly formGroupChange$ = this.formGroup$.pipe(
        switchMap(formGroup =>
            fromControlWithDebounceTime(formGroup).pipe(
                startWith(formGroup),
                map(() => formGroup),
            ),
        ),
    );

    private readonly agentTypeChange$ = this.formGroup$.pipe(
        switchMap(formGroup => fromControl(formGroup.controls.agentType)),
    );

    readonly view$: Observable<AgentInvestigationView> = combineLatest({
        formGroup: this.formGroupChange$,
        consumptionToMonitor: this.consumptionToMonitor$,
        consumptionAgentTypeInvestigations: of(CONSUMPTION_AGENT_TYPE_INVESTIGATIONS),
    });

    constructor() {
        combineLatest([this.formGroup$.pipe(take(1)), this.agentTypeChange$])
            .pipe(takeUntilDestroyed())
            .subscribe(([formGroup, agentType]) => {
                this.resetFormGroup(formGroup, agentType);
                this.updateControlStates(formGroup, agentType);
                this.updatePercentageValidator(formGroup, agentType);
            });
    }

    private resetFormGroup(
        formGroup: ConsumptionAgentInvestigationFormGroup,
        agentType: ConsumptionAgentType,
    ): void {
        const {operator} = formGroup.getRawValue();
        const validOperators = AGENT_TYPE_OPERATOR_MAP[agentType];
        formGroup.reset(
            {
                ...formGroup.value,
                agentType,
                operator: validOperators.includes(operator) ? operator : validOperators[0],
            },
            {emitEvent: false},
        );
    }

    private updateControlStates(
        formGroup: ConsumptionAgentInvestigationFormGroup,
        agentType: ConsumptionAgentType,
    ): void {
        Object.entries(AGENT_TYPE_FORM_GROUP_KEY_MAP)
            .filter(([key]) => key !== agentType)
            .flatMap(([_, value]) => value)
            .forEach(key => formGroup.controls[key].disable({emitEvent: false}));

        AGENT_TYPE_FORM_GROUP_KEY_MAP[agentType].forEach(key =>
            formGroup.controls[key].enable({emitEvent: false}),
        );
    }

    private updatePercentageValidator(
        formGroup: ConsumptionAgentInvestigationFormGroup,
        agentType: ConsumptionAgentType,
    ): void {
        const percentage = formGroup.controls.percentage;
        if (agentType === 'GROUP_COMPARISON') {
            percentage.removeValidators(Validators.required);
        } else {
            percentage.addValidators(Validators.required);
        }
        percentage.updateValueAndValidity({onlySelf: true});
    }

    getDescriptorValue(descriptor?: Translation): string {
        if (!descriptor) {
            return this.outputUnitTranslatedSubject$.value;
        }
        const {key, module, application} = descriptor;
        return this.translateService.translate(key, application, module);
    }
}
